/* ==========================================================================
   Orbly V2 — Main Theme JS
   GSAP + Lenis + Section Modules
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Feature Detection ---------- */
  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Lenis Smooth Scroll ---------- */
  let lenis;

  function initLenis() {
    // SHO-196: Lenis Smooth-Scroll deaktiviert. Lenis fängt jeden Wheel-Event ab,
    // läuft per requestAnimationFrame in einer Endlosschleife und triggert dabei
    // ScrollTrigger.update() — auf Desktops mit integrierter GPU oder älteren
    // CPUs erzeugt das spürbares Input-Lag und ein "kleben" beim Scrollen.
    // Native Wheel-Scroll ist auf praktisch allen Browsern flüssiger.
    return;
  }

  /* ---------- GSAP Setup ---------- */
  function initGSAP() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger === 'undefined') return;

    // Skip animations if user prefers reduced motion
    if (prefersReducedMotion()) return;

    gsap.registerPlugin(ScrollTrigger);

    // Performance: weniger Callbacks, iOS Address Bar ignorieren
    ScrollTrigger.config({
      ignoreMobileResize: true,
      limitCallbacks: true,
    });

    // Skip GSAP animations entirely on Product V5 pages (has its own reveal system)
    if (document.querySelector('.pv5-wrapper')) return;

    // Signal to CSS that GSAP is ready — reveals can now safely hide elements
    document.body.setAttribute('data-gsap-ready', 'true');

    var mm = gsap.matchMedia();

    mm.add('(min-width: 769px)', function() {
      // Desktop: Volle Animationen
      initReveals();
      initHeroAnimations();
      initScrollAnimations();
      initMagneticButtons();
      initTiltCards();
    });

    mm.add('(max-width: 768px)', function() {
      // Mobile: Simplere Reveals + Hero, KEIN initScrollAnimations (zu viele ScrollTrigger)
      initMobileReveals();
      initHeroAnimations();
    });
  }

  /* ---------- Section Reveal (Premium fade-scale) ---------- */
  function initReveals() {
    // General reveal on scroll — fade up + scale for premium feel (NO blur — GPU killer)
    const allReveals = document.querySelectorAll('.reveal');
    const reveals = Array.from(allReveals).filter(el => !el.closest('.pv5-wrapper'));
    reveals.forEach((el) => {
      gsap.fromTo(el, {
        opacity: 0,
        y: 30,
        scale: 0.97,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.7,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true,
        },
      });
    });

    // Staggered reveals — children cascade
    const allStaggerGroups = document.querySelectorAll('.reveal-stagger');
    const staggerGroups = Array.from(allStaggerGroups).filter(el => !el.closest('.pv5-wrapper'));
    staggerGroups.forEach((group) => {
      const children = group.children;
      gsap.fromTo(children, {
        opacity: 0,
        y: 20,
        scale: 0.97,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: group,
          start: 'top 88%',
          once: true,
        },
      });
    });
  }

  /* ---------- Mobile Reveals (simplified for performance) ---------- */
  function initMobileReveals() {
    var allReveals = document.querySelectorAll('.reveal');
    var reveals = Array.from(allReveals).filter(function(el) { return !el.closest('.pv5-wrapper'); });
    reveals.forEach(function(el) {
      gsap.fromTo(el, {
        opacity: 0,
        y: 20,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 92%',
          once: true,
        },
      });
    });

    // Staggered reveals — simpler on mobile
    var allStaggerGroups = document.querySelectorAll('.reveal-stagger');
    var staggerGroups = Array.from(allStaggerGroups).filter(function(el) { return !el.closest('.pv5-wrapper'); });
    staggerGroups.forEach(function(group) {
      var children = group.children;
      gsap.fromTo(children, {
        opacity: 0,
        y: 16,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.08,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: group,
          start: 'top 92%',
          once: true,
        },
      });
    });
  }

  /* ---------- Hero Animations ---------- */
  function initHeroAnimations() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Eyebrow
    tl.fromTo('.hero .eyebrow', {
      opacity: 0,
      y: 20,
    }, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      delay: 0.3,
    });

    // Title — one restrained reveal, no word-by-word staging
    const title = hero.querySelector('.hero__title');
    if (title) {
      tl.fromTo(title, {
        opacity: 0,
        y: 12,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.45,
      }, '-=0.2');
    }

    // Subtitle
    tl.fromTo('.hero__subtitle', {
      opacity: 0,
      y: 20,
    }, {
      opacity: 1,
      y: 0,
      duration: 0.6,
    }, '-=0.5');

    // Buttons
    tl.fromTo('.hero__actions .btn', {
      opacity: 0,
      y: 20,
      scale: 0.95,
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.5,
      stagger: 0.1,
    }, '-=0.3');

    // Hero visual — restrained composition reveal
    const visual = hero.querySelector('.hero__product-composition');
    if (visual) {
      tl.fromTo(visual, {
        opacity: 0,
        y: 12,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: 'power2.out',
      }, '-=0.35');
    }

    // Hero parallax removed for performance





















  }

  /* ---------- Premium Scroll Animations ---------- */
  function initScrollAnimations() {
    // Animate only values that are entirely numeric. Mixed values such as
    // "30-Day", "1–2 Days", and "$50+" must remain static in every state.
    const statNumbers = document.querySelectorAll('.brand-story__stat-num, .transformations__stat-number');
    statNumbers.forEach((num) => {
      const text = num.textContent.trim();
      if (!/^\d+(?:\.\d+)?$/.test(text)) return;

      const target = Number(text);
      const isDecimal = text.includes('.');

      ScrollTrigger.create({
        trigger: num,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(num, { innerText: 0 }, {
            innerText: target,
            duration: 2,
            ease: 'power2.out',
            snap: { innerText: isDecimal ? 0.1 : 1 },
            onUpdate: function() {
              num.textContent = isDecimal
                ? parseFloat(gsap.getProperty(num, 'innerText')).toFixed(1)
                : Math.round(gsap.getProperty(num, 'innerText'));
            },
          });
        },
      });
    });

    // Glass cards — staggered scale + rotation reveal
    const glassCards = document.querySelectorAll('.glass-card:not(.reveal)');
    glassCards.forEach((card, i) => {
      gsap.fromTo(card, {
        opacity: 0,
        y: 30,
        scale: 0.96,
        rotateY: 0,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        rotateY: 0,
        duration: 1.0,
        delay: i * 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: card,
          start: 'top 88%',
          once: true,
        },
      });
    });

    // Section headings — fade up reveal (SHO-196: clip-path entfernt,
    // clip-path-Animation zwingt Browser zu Layer-Repaint, war auf
    // Desktops mit integrierter GPU spürbar als kurzer Frame-Drop)
    const sectionTitles = document.querySelectorAll('.section h2:not(.hero__title):not(.reveal)');
    sectionTitles.forEach((title) => {
      gsap.fromTo(title, {
        opacity: 0,
        y: 20,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 88%',
          once: true,
        },
      });
    });

    // Eyebrow texts — slide in with line drawing effect
    const eyebrows = document.querySelectorAll('.section:not(.hero) .eyebrow:not(.reveal)');
    eyebrows.forEach((eyebrow) => {
      gsap.fromTo(eyebrow, {
        opacity: 0,
        x: -12,
      }, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: eyebrow,
          start: 'top 90%',
          once: true,
        },
      });
    });

    // Collection cards — dramatic staggered entrance
    const collectionCards = document.querySelectorAll('.collection-card:not(.reveal)');
    if (collectionCards.length) {
      gsap.fromTo(collectionCards, {
        opacity: 0,
        y: 40,
        scale: 0.95,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.0,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: collectionCards[0].parentElement,
          start: 'top 80%',
          once: true,
        },
      });
    }

    // Product cards — cascade from bottom with slight rotation
    const productCards = document.querySelectorAll('.product-card:not(.reveal)');
    if (productCards.length) {
      gsap.fromTo(productCards, {
        opacity: 0,
        y: 30,
        rotateX: 0,
      }, {
        opacity: 1,
        y: 0,
        rotateX: 0,
        duration: 0.9,
        stagger: 0.08,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: productCards[0].parentElement,
          start: 'top 85%',
          once: true,
        },
      });
    }

    // Testimonial cards — slide in from alternating sides
    const testimonialCards = document.querySelectorAll('.testimonial-card:not(.reveal)');
    testimonialCards.forEach((card, i) => {
      gsap.fromTo(card, {
        opacity: 0,
        x: i % 2 === 0 ? -20 : 20,
        y: 16,
      }, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 1.0,
        delay: i * 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: card,
          start: 'top 88%',
          once: true,
        },
      });
    });

    // Brand story visual — elegant scale-in with glow
    const brandVisual = document.querySelector('.brand-story__visual:not(.reveal)');
    if (brandVisual) {
      gsap.fromTo(brandVisual, {
        opacity: 0,
        
        y: 16,
      }, {
        opacity: 1,
        
        y: 0,
        duration: 1.4,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: brandVisual,
          start: 'top 80%',
          once: true,
        },
      });
    }

    // Brand story text — fade in from below
    const brandText = document.querySelector('.brand-story__text:not(.reveal)');
    if (brandText) {
      gsap.fromTo(brandText, {
        opacity: 0,
        y: 16,
      }, {
        opacity: 1,
        y: 0,
        duration: 1.2,
        delay: 0.3,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: brandText,
          start: 'top 80%',
          once: true,
        },
      });
    }

    // Brand story stats — pop in with stagger
    const brandStats = document.querySelectorAll('.brand-story__stat:not(.reveal)');
    if (brandStats.length) {
      gsap.fromTo(brandStats, {
        opacity: 0,
        y: 20,
        scale: 0.95,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: brandStats[0].parentElement,
          start: 'top 85%',
          once: true,
        },
      });
    }

    // Newsletter CTA — bounce-in envelope
    const newsletterSection = document.querySelector('#newsletter .glass-card:not(.reveal)');
    if (newsletterSection) {
      gsap.fromTo(newsletterSection, {
        opacity: 0,
        y: 30,
        scale: 0.96,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: newsletterSection,
          start: 'top 82%',
          once: true,
        },
      });
    }

    // FAQ items — stagger from left
    const faqItems = document.querySelectorAll('.faq-item:not(.reveal)');
    if (faqItems.length) {
      gsap.fromTo(faqItems, {
        opacity: 0,
        x: -10,
        y: 8,
      }, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: faqItems[0].parentElement,
          start: 'top 82%',
          once: true,
        },
      });
    }

    // Team cards — dramatic stagger pop
    const teamCards = document.querySelectorAll('.team__card:not(.reveal)');
    if (teamCards.length) {
      gsap.fromTo(teamCards, {
        opacity: 0,
        y: 30,
        scale: 0.95,
        rotateY: 0,
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        rotateY: 0,
        duration: 1.0,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: teamCards[0].parentElement,
          start: 'top 82%',
          once: true,
        },
      });
    }

    // Comparison table rows — slide in from right
    const compRows = document.querySelectorAll('.comparison__row');
    if (compRows.length) {
      gsap.fromTo(compRows, {
        opacity: 0,
        x: 30,
      }, {
        opacity: 1,
        x: 0,
        duration: 0.5,
        stagger: 0.06,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: compRows[0].parentElement,
          start: 'top 80%',
          once: true,
        },
      });
    }

    // Transformations before/after slider — dramatic reveal
    const transformSlider = document.querySelector('.transformations__slider');
    if (transformSlider) {
      gsap.fromTo(transformSlider, {
        opacity: 0,
        scale: 0.85,
        y: 40,
      }, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: transformSlider,
          start: 'top 80%',
          once: true,
        },
      });
    }

    // SHO-196: Section-Parallax (backgroundPositionY mit scrub:2) entfernt.
    // Dieser ScrollTrigger lief auf jeder Sektion in einer Endlosschleife
    // beim Scrollen und war der größte Single-Frame-Fresser auf Desktop.
  }

  /* ---------- Magnetic Buttons ---------- */
  function initMagneticButtons() {
    // SHO-196: deaktiviert. mousemove + rAF auf jedem .btn-primary / .btn-secondary
    // erzeugte spürbares Lag beim Hover über die Hero/CTA-Bereiche. Hover-Effekte
    // werden weiterhin über CSS in theme.css/components.css gerendert.
    return;
  }

  /* ---------- 3D Tilt Cards ---------- */
  function initTiltCards() {
    // SHO-196: deaktiviert. Selbe Begründung wie Magnetic Buttons —
    // mousemove + rAF auf jeder .tilt-card war ein zweiter Hot Path
    // beim Cursor-Bewegung über die Landing.
    return;
  }

  /* ---------- FAQ Accordion ---------- */
  function initFaqAccordion() {
    const hasGSAP = typeof gsap !== 'undefined';
    const items = document.querySelectorAll('.faq-item');
    items.forEach((item) => {
      const question = item.querySelector('.faq-item__question');
      const answer = item.querySelector('.faq-item__answer');
      if (!question || !answer) return;

      question.addEventListener('click', () => {
        const isActive = item.classList.contains('active');

        // Close all
        items.forEach((other) => {
          if (other !== item) {
            other.classList.remove('active');
            const otherAnswer = other.querySelector('.faq-item__answer');
            if (hasGSAP) {
              gsap.to(otherAnswer, { height: 0, duration: 0.4, ease: 'power3.inOut' });
            } else {
              otherAnswer.style.height = '0';
            }
          }
        });

        // Toggle current
        if (isActive) {
          item.classList.remove('active');
          if (hasGSAP) {
            gsap.to(answer, { height: 0, duration: 0.4, ease: 'power3.inOut' });
          } else {
            answer.style.height = '0';
          }
        } else {
          item.classList.add('active');
          if (hasGSAP) {
            gsap.to(answer, { height: 'auto', duration: 0.4, ease: 'power3.inOut' });
          } else {
            answer.style.height = 'auto';
          }
        }
      });
    });
  }

  /* ---------- Header Scroll ---------- */
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    // SHO-196: rAF-throttle. Vorher feuerte der Handler auf jedem scroll-Event
    // (i.d.R. mehrere pro Frame) und schrieb in DOM-Klassen + style.transform,
    // was Layout-/Paint-Arbeit triggerte.
    let lastScroll = 0;
    let ticking = false;

    function update() {
      const scrollY = window.scrollY;
      if (scrollY > 50) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }

      // Hide on scroll down, show on scroll up
      if (scrollY > lastScroll && scrollY > 200) {
        header.style.transform = 'translateY(-100%)';
      } else {
        header.style.transform = 'translateY(0)';
      }
      lastScroll = scrollY;
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ---------- Back to Top ---------- */
  function initBackToTop() {
    const btn = document.querySelector('.back-to-top');
    if (!btn) return;

    // SHO-196: rAF-throttle (siehe initHeaderScroll).
    let btnTicking = false;
    function btnUpdate() {
      if (window.scrollY > 600) {
        btn.classList.add('visible');
      } else {
        btn.classList.remove('visible');
      }
      btnTicking = false;
    }
    window.addEventListener('scroll', () => {
      if (btnTicking) return;
      btnTicking = true;
      requestAnimationFrame(btnUpdate);
    }, { passive: true });

    btn.addEventListener('click', () => {
      if (lenis) {
        lenis.scrollTo(0);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  /* ---------- Cart Drawer ---------- */
  function initCartDrawer() {
    const nativeDrawer = document.querySelector('cart-drawer');
    const openBtns = document.querySelectorAll('[data-cart-open]');

    async function updateHeaderCount() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        const count = Number(cart.item_count || 0);
        const cartBtn = document.querySelector('[data-cart-open]');
        if (!cartBtn) return;

        let badge = cartBtn.querySelector('.cart-count');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'cart-count';
            cartBtn.appendChild(badge);
          }
          badge.textContent = String(count);
        } else if (badge) {
          badge.remove();
        }
      } catch (e) {
        console.warn('[Cart] header count update failed', e);
      }
    }

    /* Always listen for cart:refresh — works on all pages (product, cart, etc.) */
    document.addEventListener("cart:refresh", updateHeaderCount);
    updateHeaderCount();
    if (nativeDrawer) {
      // cleanup any stale lock/overlay classes from previous broken states
      document.body.classList.remove('overflow-hidden');
      document.body.style.overflow = '';
      document.querySelectorAll('#CartDrawer-Overlay, .cart-drawer__overlay').forEach((el) => el.classList.remove('open', 'active'));
      document.querySelectorAll('.cart-drawer, #CartDrawer, .drawer').forEach((el) => el.classList.remove('open', 'active'));

      // Force one reliable drawer-first trigger path (desktop + iOS Safari)
      // and block conflicting handlers from navigating to /cart.
      if (!window.__orblyCartTriggerBound) {
        window.__orblyCartTriggerBound = true;

        const openNativeDrawer = (btn) => {
          if (!nativeDrawer) return;

          const trigger = btn || document.querySelector('[data-cart-open]');

          if (typeof nativeDrawer.open === 'function') {
            nativeDrawer.open(trigger);
          } else {
            // Hard fallback when custom element method isn't available
            nativeDrawer.classList.add('animate', 'active');
            const rawDrawer = nativeDrawer.querySelector('#CartDrawer') || document.getElementById('CartDrawer');
            const overlay = nativeDrawer.querySelector('#CartDrawer-Overlay') || document.getElementById('CartDrawer-Overlay');
            if (rawDrawer) rawDrawer.classList.add('active');
            if (overlay) overlay.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }

          if (trigger && typeof trigger.blur === 'function') trigger.blur();
        };

        window.__orblyOpenCart = openNativeDrawer;

        document.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-cart-open]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          openNativeDrawer(btn);
        }, true);

        document.addEventListener('touchend', (e) => {
          const btn = e.target.closest('[data-cart-open]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          openNativeDrawer(btn);
        }, { capture: true, passive: false });
      }

      document.addEventListener('cart:refresh', updateHeaderCount);
      if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
          updateHeaderCount();
          document.dispatchEvent(new Event('cart:refresh'));
        });
      }

      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return;
        [250, 600, 1200].forEach((ms) => setTimeout(updateHeaderCount, ms));
      }, true);

      updateHeaderCount();
      return;
    }

    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) return;

    function openCart() {
      drawer.classList.add('open');
      if (overlay) overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    function closeCart() {
      drawer.classList.remove('open');
      if (overlay) overlay.classList.remove('open');
      document.body.style.overflow = '';
    }

    openBtns.forEach((btn) => btn.addEventListener('click', openCart));
    if (overlay) overlay.addEventListener('click', closeCart);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        closeCart();
      }
    });
  }

  

  /* ---------- Mobile Menu — Premium Slide-in ---------- */
  function initMobileMenu() {
    const toggle = document.querySelector('.menu-toggle');
    const nav = document.getElementById('mobile-nav');
    const backdrop = document.getElementById('mobile-nav-backdrop');
    if (!toggle || !nav) return;

    const openNav = () => {
      toggle.classList.add('active');
      nav.classList.add('open');
      if (backdrop) backdrop.classList.add('open');
      nav.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mobile-nav-open');
      const firstLink = nav.querySelector('.mobile-nav__link');
      if (firstLink) setTimeout(() => firstLink.focus(), 250);
    };

    const closeNav = () => {
      toggle.classList.remove('active');
      nav.classList.remove('open');
      if (backdrop) backdrop.classList.remove('open');
      nav.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-nav-open');
      toggle.focus();
    };

    toggle.addEventListener('click', () => {
      nav.classList.contains('open') ? closeNav() : openNav();
    });

    if (backdrop) backdrop.addEventListener('click', closeNav);

    const closeBtn = nav.querySelector('.mobile-nav__close');
    if (closeBtn) closeBtn.addEventListener('click', closeNav);

    nav.querySelectorAll('.mobile-nav__link').forEach(link => {
      link.addEventListener('click', closeNav);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('open')) closeNav();
    });

    // Swipe right to close
    let touchStartX = 0, touchStartY = 0;
    nav.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    nav.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
      if (dx > 80 && dy < 100 && nav.classList.contains('open')) closeNav();
    }, { passive: true });
  }

  /* ---------- Paw Stamp Add-to-Cart ---------- */
  function initPawStamp() {
    const btns = document.querySelectorAll('.btn-paw-stamp');
    btns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        btn.classList.add('stamped');
        setTimeout(() => btn.classList.remove('stamped'), 700);
      });
    });
  }

  /* ---------- Page Transitions ---------- */
  function initPageTransitions() {
    if (prefersReducedMotion()) return;

    const transitionEl = document.querySelector('.page-transition');
    if (!transitionEl) return;

    // Exit animation on link click
    document.querySelectorAll('a[href]').forEach((link) => {
      if (link.hostname !== window.location.hostname) return;
      if (link.getAttribute('href').startsWith('#')) return;
      if (link.hasAttribute('data-no-transition')) return;

      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        e.preventDefault();
        transitionEl.classList.add('enter');
        setTimeout(() => {
          window.location.href = href;
        }, 500);
      });
    });

    // Enter animation on page load
    window.addEventListener('pageshow', () => {
      transitionEl.classList.remove('enter');
      transitionEl.classList.add('exit');
      setTimeout(() => {
        transitionEl.classList.remove('exit');
      }, 600);
    });
  }

  /* ---------- Testimonials Carousel ---------- */
  function initTestimonialsCarousel() {
    const carousel = document.querySelector('.testimonials__carousel');
    if (!carousel) return;

    const prevBtn = document.querySelector('[data-testimonial-prev]');
    const nextBtn = document.querySelector('[data-testimonial-next]');

    function getScrollAmount() {
      /* Scroll by the visible width of the carousel (shows 3 cards on desktop, 1 on mobile) */
      return carousel.clientWidth;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        carousel.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        carousel.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      });
    }
  }

  /* ---------- Loading Screen ---------- */
  function initLoadingScreen() {
    const loading = document.querySelector('.loading-screen');
    if (!loading) return;

    function hideLoader() {
      if (loading.classList.contains('hidden')) return;
      loading.classList.add('hidden');
      setTimeout(() => loading.remove(), 600);
    }

    window.addEventListener('load', () => setTimeout(hideLoader, 400));
    setTimeout(hideLoader, 3000);
  }

  /* ---------- Product Gallery ---------- */
  function initProductGallery() {
    const thumbnails = document.querySelectorAll('.product-page__thumbnail');
    const mainImage = document.querySelector('.product-page__main-image img');
    const prevBtn = document.querySelector('[data-gallery-prev]');
    const nextBtn = document.querySelector('[data-gallery-next]');
    const dots = document.querySelectorAll('.product-page__dot');
    if (!mainImage || !thumbnails.length) return;

    let currentIndex = 0;
    const images = Array.from(thumbnails);

    function goToImage(index) {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      currentIndex = index;

      const thumb = images[index];
      const newSrc = thumb.querySelector('img')?.dataset?.fullSrc || thumb.querySelector('img')?.src;

      thumbnails.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      dots.forEach(d => d.classList.remove('active'));
      if (dots[index]) dots[index].classList.add('active');

      // Scroll thumbnail into view
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

      if (newSrc && mainImage.src !== newSrc) {
        gsap.to(mainImage, {
          opacity: 0,
          duration: 0.15,
          onComplete: () => {
            mainImage.src = newSrc;
            gsap.to(mainImage, { opacity: 1, duration: 0.25 });
          },
        });
      }
    }

    thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goToImage(i));
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goToImage(i));
    });

    if (prevBtn) prevBtn.addEventListener('click', () => goToImage(currentIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goToImage(currentIndex + 1));

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!document.querySelector('.product-page')) return;
      if (e.key === 'ArrowLeft') goToImage(currentIndex - 1);
      if (e.key === 'ArrowRight') goToImage(currentIndex + 1);
    });
  }

  /* ---------- Product Tabs ---------- */
  function initProductTabs() {
    const tabHeaders = document.querySelectorAll('.product-page__tab-header');
    tabHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const tab = header.closest('.product-page__tab');
        const isOpen = tab.classList.contains('open');

        // Close all tabs
        document.querySelectorAll('.product-page__tab').forEach(t => t.classList.remove('open'));

        // Toggle clicked tab
        if (!isOpen) {
          tab.classList.add('open');
          header.setAttribute('aria-expanded', 'true');
        } else {
          header.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ---------- Variant Picker ---------- */
  function initVariantPicker() {
    const options = document.querySelectorAll('.variant-picker__option');
    options.forEach((option) => {
      option.addEventListener('click', () => {
        const group = option.closest('.variant-picker');
        group.querySelectorAll('.variant-picker__option').forEach((o) => o.classList.remove('selected'));
        option.classList.add('selected');
      });
    });
  }

  /* ---------- Quantity Selector ---------- */
  function initQuantitySelector() {
    document.querySelectorAll('.quantity-selector').forEach((selector) => {
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');
      const input = selector.querySelector('.quantity-selector__value');
      if (!minus || !plus || !input) return;

      minus.addEventListener('click', () => {
        const val = parseInt(input.value) || 1;
        if (val > 1) input.value = val - 1;
      });

      plus.addEventListener('click', () => {
        const val = parseInt(input.value) || 1;
        input.value = val + 1;
      });
    });
  }

  /* ---------- Before/After Compare Slider ---------- */
  function initCompareSlider() {
    document.querySelectorAll('[data-compare-slider]').forEach((slider) => {
      const beforeEl = slider.querySelector('.transformations__before');
      const handle = slider.querySelector('.transformations__handle');
      if (!beforeEl || !handle) return;

      let isDragging = false;

      function updateSlider(x) {
        const rect = slider.getBoundingClientRect();
        let pos = ((x - rect.left) / rect.width) * 100;
        pos = Math.max(0, Math.min(100, pos));
        beforeEl.style.clipPath = `polygon(0 0, ${pos}% 0, ${pos}% 100%, 0 100%)`;
        handle.style.left = pos + '%';
        handle.setAttribute('aria-valuenow', Math.round(pos));
      }

      slider.addEventListener('mousedown', (e) => { isDragging = true; updateSlider(e.clientX); }, { passive: true });
      document.addEventListener('mousemove', (e) => { if (isDragging) updateSlider(e.clientX); }, { passive: true });
      document.addEventListener('mouseup', () => { isDragging = false; }, { passive: true });

      slider.addEventListener('touchstart', (e) => { isDragging = true; updateSlider(e.touches[0].clientX); }, { passive: false });
      document.addEventListener('touchmove', (e) => { if (isDragging) { e.preventDefault(); updateSlider(e.touches[0].clientX); } }, { passive: false });
      document.addEventListener('touchend', () => { isDragging = false; });

      // Keyboard support
      handle.addEventListener('keydown', (e) => {
        const current = parseFloat(handle.style.left) || 50;
        if (e.key === 'ArrowLeft') updateSlider(slider.getBoundingClientRect().left + (current - 2) / 100 * slider.offsetWidth);
        if (e.key === 'ArrowRight') updateSlider(slider.getBoundingClientRect().left + (current + 2) / 100 * slider.offsetWidth);
      });
    });
  }

  /* ---------- Collections Swipe Dots ---------- */
  function initCollectionsDots() {
    const grid = document.querySelector('.featured-collections__grid');
    const dots = document.querySelectorAll('.collections__dot');
    if (!grid || !dots.length) return;

    grid.addEventListener('scroll', () => {
      const scrollLeft = grid.scrollLeft;
      const cardWidth = grid.querySelector('.collection-card')?.offsetWidth || 1;
      const gap = 24;
      const idx = Math.round(scrollLeft / (cardWidth + gap));
      dots.forEach((d, i) => d.classList.toggle('active', i === idx));
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        const card = grid.querySelector('.collection-card');
        if (!card) return;
        const cardWidth = card.offsetWidth;
        const gap = 24;
        grid.scrollTo({ left: idx * (cardWidth + gap), behavior: 'smooth' });
      });
    });
  }

  /* ---------- Team Flip Card (mobile) ---------- */
  function initTeamFlip() {
    const wrapper = document.querySelector('.team__flip-wrapper');
    const card = document.getElementById('teamFlipCard');
    if (!wrapper || !card) return;

    wrapper.addEventListener('click', () => {
      card.classList.toggle('flipped');
    });
  }





  function initCartBadgeSync() {
    const sync = async () => {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return;
        const cart = await res.json();
        const btn = document.getElementById('cart-icon-bubble');
        if (!btn) return;
        let bubble = btn.querySelector('.cart-count');
        const count = Number(cart.item_count || 0);
        if (count > 0) {
          if (!bubble) {
            bubble = document.createElement('span');
            bubble.className = 'cart-count';
            btn.appendChild(bubble);
          }
          bubble.textContent = String(count);
        } else if (bubble) {
          bubble.remove();
        }
      } catch (_) {}
    };

    sync();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) sync(); });

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.matches('form[action*="/cart/add"]')) {
        setTimeout(sync, 400);
        setTimeout(sync, 1200);
      }
    }, true);

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="add"], .product-form__submit, .sticky-atc__button button');
      if (btn) {
        setTimeout(sync, 500);
        setTimeout(sync, 1500);
      }
    }, true);
  }

  
  /* ---------- Count-Up Animation for Stats ---------- */
  function initCountUp() {
    var els = document.querySelectorAll('[data-count-up]');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    function parse(text) {
      text = text.trim();
      // Keep mixed-format values static. This path is numeric-only, matching
      // initScrollAnimations above, so ranges/prefixes/suffixes never flash 0.
      if (!/^\d+(?:,\d{3})*(?:\.\d+)?$/.test(text)) return null;
      var num = parseFloat(text.replace(/,/g, ''));
      if (isNaN(num)) return null;
      var dec = text.indexOf('.') > -1 ? text.split('.')[1].length : 0;
      return { target: num, suffix: '', decimals: dec };
    }

    function animate(el, p) {
      var duration = 1800, start = null;
      function ease(t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }
      function step(ts) {
        if (!start) start = ts;
        var prog = Math.min((ts - start) / duration, 1);
        var val = ease(prog) * p.target;
        el.textContent = (p.decimals > 0 ? val.toFixed(p.decimals) : Math.round(val).toLocaleString('en-US')) + p.suffix;
        if (prog < 1) requestAnimationFrame(step);
      }
      el.textContent = (p.decimals > 0 ? (0).toFixed(p.decimals) : '0') + p.suffix;
      requestAnimationFrame(step);
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var p = parse(el.getAttribute('data-count-original'));
          if (p) animate(el, p);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -20px 0px' });

    els.forEach(function(el) {
      el.setAttribute('data-count-original', el.textContent.trim());
      var p = parse(el.textContent.trim());
      if (p) {
        el.textContent = (p.decimals > 0 ? (0).toFixed(p.decimals) : '0') + p.suffix;
        observer.observe(el);
      }
    });
  }

  /* ---------- Initialize Everything ---------- */
  function init() {
    initLoadingScreen();
    initLenis();
    initGSAP();
    initHeaderScroll();
    initPawStamp();
    initTestimonialsCarousel();
    initProductGallery();
    initProductTabs();
    initVariantPicker();
    initQuantitySelector();
    initCompareSlider();

    // UI functions that work without GSAP
    initFaqAccordion();
    initBackToTop();
    initCartDrawer();
    initCartBadgeSync();
    initMobileMenu();
    initTeamFlip();
    initCollectionsDots();
    initCountUp();

    // Only init page transitions if enabled
    const body = document.body;
    if (body.dataset.pageTransitions === 'true') {
      initPageTransitions();
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
